import { useEffect, useState } from "react";
import { Check, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOWN = ["starter", "professional", "business", "enterprise"];

const money = (n) => `$${Number(n).toLocaleString()}`;

/* What each tier actually buys, in the operator's words. Every plan carries the whole platform —
   tiers differ only by how much operation they hold — so the bullets say exactly that. */
const bullets = (p) => {
  const rows = [];
  if (p.max_properties) rows.push(`${p.max_properties} properties included`);
  else rows.push("Unlimited properties");
  if (p.max_users) rows.push(`${p.max_users} team members`);
  else rows.push("Unlimited team members");
  if (p.overage_rate) rows.push(`${money(p.overage_rate)}/month per extra property — no tier jump`);
  rows.push("The complete platform: scheduling, inspections, vendors, payments, weather");
  if (p.contact_only) rows.push("White-label, API access and dedicated onboarding");
  return rows;
};

export const PricingSection = ({ onStart }) => {
  const [plans, setPlans] = useState(null);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    let live = true;
    fetch(`${API}/public/plans`)
      .then((r) => r.json())
      .then((d) => live && setPlans((d.plans || []).filter((p) => SHOWN.includes(p.value))))
      .catch(() => live && setPlans([]));
    return () => {
      live = false;
    };
  }, []);

  return (
    <section id="pricing" data-testid="pricing-section" className="scroll-mt-20 bg-background px-4 py-16 sm:px-6 sm:py-20">
      <div className="mx-auto max-w-6xl">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gold">Pricing</p>
        <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-royal-deep sm:text-4xl">
          Every price, right here.
        </h2>
        <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
          No quote form, no sales call to find out what it costs. Pick the size of your operation.
        </p>

        <div
          className="mt-7 inline-flex rounded-full border border-border bg-white p-1"
          data-testid="pricing-toggle"
        >
          {[
            { id: false, text: "Monthly" },
            { id: true, text: "Annual" },
          ].map((o) => (
            <button
              key={String(o.id)}
              type="button"
              data-testid={`pricing-toggle-${o.id ? "annual" : "monthly"}`}
              onClick={() => setAnnual(o.id)}
              className={`min-h-[44px] rounded-full px-5 text-sm font-semibold transition-colors duration-200 ${
                annual === o.id ? "bg-royal text-white" : "text-royal hover:text-gold"
              }`}
            >
              {o.text}
              {o.id && <span className="ml-1.5 text-[11px] opacity-80">2 months free</span>}
            </button>
          ))}
        </div>

        {!plans ? (
          <Loader2 className="mt-10 h-6 w-6 animate-spin text-royal" data-testid="pricing-loading" />
        ) : (
          <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((p) => {
              const saving = p.price && p.annual_price ? p.price * 12 - p.annual_price : 0;
              return (
                <div
                  key={p.value}
                  data-testid={`pricing-tier-${p.value}`}
                  className={`relative flex flex-col rounded-2xl border bg-white p-6 transition-shadow duration-200 hover:shadow-lg ${
                    p.popular ? "border-gold shadow-md ring-1 ring-gold/40" : "border-border"
                  }`}
                >
                  {p.popular && (
                    <span
                      data-testid="pricing-popular-badge"
                      className="absolute -top-3 left-6 rounded-full bg-gold px-3 py-1 font-display text-[10px] font-bold uppercase tracking-[0.16em] text-royal-deep"
                    >
                      Most popular
                    </span>
                  )}
                  <p className="font-display text-xl font-bold text-royal-deep">{p.label}</p>
                  <p className="mt-1 text-[11px] uppercase tracking-[0.14em] text-muted-foreground">
                    {p.scale}
                  </p>

                  <div className="mt-5">
                    {p.contact_only ? (
                      <>
                        <p className="font-display text-3xl font-bold text-royal-deep">
                          From {money(p.starting_price)}
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">per month, scoped to your portfolio</p>
                      </>
                    ) : (
                      <>
                        <p className="font-display text-4xl font-bold text-royal-deep">
                          {money(annual ? Math.round(p.annual_price / 12) : p.price)}
                          <span className="font-sans text-sm font-semibold text-muted-foreground">/mo</span>
                        </p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {annual
                            ? `${money(p.annual_price)} billed yearly — save ${money(saving)}`
                            : `per month, billed monthly`}
                        </p>
                      </>
                    )}
                  </div>

                  <ul className="mt-5 flex-1 space-y-2.5">
                    {bullets(p).map((b) => (
                      <li key={b} className="flex gap-2 text-sm text-royal-deep/90">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    data-testid={`pricing-start-${p.value}`}
                    onClick={onStart}
                    className={`mt-6 min-h-[48px] w-full rounded-lg font-display text-sm font-bold transition-colors duration-200 ${
                      p.popular
                        ? "bg-gold text-royal-deep hover:bg-gold/90"
                        : "bg-royal text-white hover:bg-royal-deep"
                    }`}
                  >
                    Start Your 14-Day Free Trial
                  </button>
                </div>
              );
            })}
          </div>
        )}

        <p
          className="mt-8 text-center text-sm font-semibold text-royal-deep"
          data-testid="pricing-no-addons"
        >
          All plans include the full operations platform. No add-ons. No hidden fees.
        </p>
      </div>
    </section>
  );
};
